import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './MonthYearPicker.css';

/**
 * MonthYearPicker — choisir un MOIS et une ANNÉE d'un geste (2026-09-26).
 *
 * Deux listes déroulantes côte à côte demandaient deux ouvertures pour une
 * seule date, et l'année se tapait au clavier. Ici, un bouton qui dit la date
 * ouvre un petit panneau : l'année en tête avec ses flèches, les douze mois en
 * grille de trois. Un clic sur un mois choisit ET referme.
 *
 * Ce n'est pas une fenêtre de navigation (proscrites) : c'est le réglage d'une
 * valeur, ancré à son champ.
 *
 * ⚠️ LE PANNEAU SORT PAR PORTAIL : ancré en position absolue, il était COUPÉ
 * par le `overflow` de la table qui défile (`.lk-hscroll`) et de la carte. Il
 * se rend donc sur `document.body`, en position fixe calculée depuis le bouton.
 */
/* `closedUntil` ({ year, month }) GRISE les mois clos et ceux d'avant
   (2026-10-03) : une acquisition ne se date pas dans un mois que la
   comptabilité a déjà arrêté. `closedTitle` dit pourquoi, au survol. */
export default function MonthYearPicker({ month, year, onChange, canWrite = true, ariaLabel,
                                          closedUntil = null, closedTitle = null }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [shownYear, setShownYear] = useState(year || new Date().getFullYear());
  const box = useRef(null);
  const pop = useRef(null);
  const [at, setAt] = useState(null);

  useEffect(() => { if (open) setShownYear(year || new Date().getFullYear()); }, [open, year]);
  /* La place se mesure à l'ouverture, et se recale si le panneau déborde du
     bas ou de la droite de la fenêtre. */
  useEffect(() => {
    if (!open || !box.current) { setAt(null); return undefined; }
    const place = () => {
      const r = box.current.getBoundingClientRect();
      const W = 216; const H = 190;
      setAt({
        top: r.bottom + 4 + H > window.innerHeight ? Math.max(4, r.top - 4 - H) : r.bottom + 4,
        left: Math.max(4, Math.min(r.left, window.innerWidth - W - 4)),
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place); };
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => {
      if (box.current && box.current.contains(e.target)) return;
      if (pop.current && pop.current.contains(e.target)) return;
      setOpen(false);
    };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  const label = month && year
    ? `${t(`common:months.${month}`)} ${year}`
    : '—';

  return (
    <span className="lk-my" ref={box}>
      <button type="button" className="lk-my__b" aria-label={ariaLabel} aria-expanded={open}
              disabled={!canWrite} onClick={() => setOpen((v) => !v)}>
        {label}
      </button>
      {open && at && createPortal((
        <div className="lk-my__pop" role="dialog" ref={pop} style={{ top: at.top, left: at.left }}>
          <div className="lk-my__y">
            <button type="button" onClick={() => setShownYear((y) => y - 1)} aria-label="−1">
              <ChevronLeft size={15} />
            </button>
            <b>{shownYear}</b>
            <button type="button" onClick={() => setShownYear((y) => y + 1)} aria-label="+1">
              <ChevronRight size={15} />
            </button>
          </div>
          <div className="lk-my__g">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
              const closed = !!closedUntil
                && (shownYear < closedUntil.year || (shownYear === closedUntil.year && m <= closedUntil.month));
              return (
                <button key={m} type="button" disabled={closed} title={closed && closedTitle ? closedTitle : undefined}
                        className={`lk-my__m${(m === month && shownYear === year) ? ' is-on' : ''}`}
                        onClick={() => { onChange(m, shownYear); setOpen(false); }}>
                  {t(`common:months.${m}`)}
                </button>
              );
            })}
          </div>
        </div>
      ), document.body)}
    </span>
  );
}
